/**
 * T305 · Refresh ladder — pre-expiry timer that schedules and re-arms the
 * mid-session JWT refresh chain. Spec file 05 §L1494-L1530.
 *
 * Responsibility split:
 *
 *   - This module owns the **scheduler**: a single, HMR-pinned `setTimeout`
 *     that fires at `expiresAt − REFRESH_LEAD_TIME_MS` (default: 10 min).
 *   - The 30 / 60 / 120 s retry ladder that runs the actual mint lives in
 *     `mintCredentials` (T301).
 *   - The 7-step refresh chain (mint → store.set → snapshot lastSeq →
 *     nc.reconnect → bulk-snapshot reconcile) lives in
 *     `connectivity/jwt-refresh.ts` (T306 / T3016) as `refreshJwtMidSession`.
 *   - The terminal `DEGRADED_NO_NATS(cause:'jwt-refresh-failed')` flip is
 *     emitted by `refreshJwtMidSession` on retry-ladder exhaustion (see
 *     `connectivity/jwt-refresh.ts::transition` call). This ladder simply
 *     stops re-arming when the inner chain returns `success: false`.
 *
 * Why the schedule lives here and not in the planner:
 *
 *   The planner (`refreshJwtMidSession`) is a single-shot procedure — it
 *   runs the 7-step chain once and returns. Something has to (a) fire it
 *   at the right time pre-expiry, and (b) re-arm a new timer for the newly
 *   minted JWT's expiry. That responsibility is THIS module. Keeping the
 *   two concerns separate means the planner stays a pure, easily testable
 *   procedure with no time-driven side-effects of its own.
 *
 * HMR pinning:
 *
 *   The timer handle is pinned on `globalThis.__morfi_refreshTimer` so a
 *   Fast Refresh in dev doesn't leak dead timers. On every `scheduleRefresh`
 *   call we clear any previously-pinned handle before scheduling a new one.
 *   `cancelRefresh()` clears the pin and is idempotent — safe to call from
 *   the logout path and from the test resetter.
 *
 * TTL profile (file 05 §1494):
 *
 *   - anon JWT: 2 h TTL → first refresh at exp − 10 min (~0.5 reconnects/hr).
 *   - user JWT: 4 h TTL → first refresh at exp − 10 min (~0.27 reconnects/hr).
 *
 * The lead-time is constant across `kind`; the cadence falls out of the
 * TTL the backend stamps on each minted JWT.
 */

import { REFRESH_LEAD_TIME_MS, timeUntilNextRefresh } from '../../connectivity/jwt/jwt-refresh.js';

/** Re-export so call sites import the constant from one canonical place. */
export { REFRESH_LEAD_TIME_MS } from '../../connectivity/jwt/jwt-refresh.js';

// ─────────────────────────────────────────────────────────────────────────
// HMR-pinned timer handle
// ─────────────────────────────────────────────────────────────────────────

const TIMER_PIN_KEY = '__morfi_refreshTimer' as const;
const STATE_PIN_KEY = '__morfi_refreshTimerState' as const;

declare global {
  var __morfi_refreshTimer: unknown | undefined;
  var __morfi_refreshTimerState: RefreshTimerState | undefined;
}

/**
 * Devtool peek state for the refresh ladder. Companion to the opaque
 * timer-handle pin — exposes the wall-clock the next refresh will fire
 * at (and what JWT expiry it was scheduled against) so observability
 * surfaces can show "refresh in 9 min 14 s" without having to inspect
 * the setTimeout handle.
 */
export interface RefreshTimerState {
  /** Wall-clock ms the timer is set to fire. */
  readonly nextRefreshAt: number;
  /** Wall-clock ms `scheduleRefresh` was called. */
  readonly scheduledAt: number;
  /** The JWT expiresAt this schedule was computed against. */
  readonly expiresAt: number;
}

type GlobalWithTimer = typeof globalThis & {
  [TIMER_PIN_KEY]?: unknown;
  [STATE_PIN_KEY]?: RefreshTimerState | undefined;
};

/**
 * Pluggable timer surface. Production callers leave these undefined and
 * the module falls back to `globalThis.setTimeout` / `clearTimeout`. Tests
 * inject deterministic resolvers so the scheduler can be exercised without
 * a real timer queue.
 */
export interface RefreshLadderTimerOpts {
  readonly setTimeoutFn?: (handler: () => void, ms: number) => unknown;
  readonly clearTimeoutFn?: (handle: unknown) => void;
  readonly now?: () => number;
}

/** Delay between refresh re-arms after the inner chain reports failure. */
export const REFRESH_FAILURE_RETRY_MS = 60_000;
/** Stop re-arming this close to the real expiry; the closed() watcher takes over. */
export const REFRESH_FAILURE_GRACE_MS = 30_000;

interface ScheduleArgs extends RefreshLadderTimerOpts {
  /** Epoch-ms expiry of the JWT just minted. Required. */
  readonly expiresAt: number;
  /**
   * The real token expiry when `expiresAt` is a synthetic re-arm target
   * (failure retry). Internal — callers never set it.
   */
  readonly realExpiresAt?: number;
  /**
   * The actual refresh routine — typically a bound `refreshJwtMidSession`
   * with all deps wired up. The ladder calls this exactly once when the
   * timer fires.
   *
   * MUST return `success: boolean` so the ladder knows whether to re-arm.
   * On `success: false` the inner chain has already emitted the terminal
   * `DEGRADED_NO_NATS(cause:'jwt-refresh-failed')` transition; the ladder
   * stops re-arming.
   *
   * On `success: true` the function ALSO returns the new `nextExpiresAt`
   * for the freshly minted JWT so the ladder can re-arm.
   */
  readonly runRefresh: () => Promise<RefreshOutcome>;
}

export interface RefreshOutcome {
  readonly success: boolean;
  /** Epoch-ms of the new JWT's expiry. Required on `success: true`. */
  readonly nextExpiresAt?: number;
}

function getSetTimeout(
  fn: ((h: () => void, ms: number) => unknown) | undefined,
): (h: () => void, ms: number) => unknown {
  if (fn !== undefined) return fn;
  return (h: () => void, ms: number): unknown =>
    (globalThis as { setTimeout: typeof setTimeout }).setTimeout(h, ms);
}

function getClearTimeout(fn: ((h: unknown) => void) | undefined): (h: unknown) => void {
  if (fn !== undefined) return fn;
  return (h: unknown): void =>
    (globalThis as { clearTimeout: typeof clearTimeout }).clearTimeout(
      h as ReturnType<typeof setTimeout>,
    );
}

function getNow(fn: (() => number) | undefined): () => number {
  return fn ?? ((): number => Date.now());
}

/**
 * Cancel any pinned refresh timer. Idempotent — safe to call from the
 * logout path, the connectivity-machine teardown, and the test resetter.
 *
 * Pass the same `clearTimeoutFn` used at schedule-time to keep test fake
 * timers consistent. Production omits both arguments.
 */
export function cancelRefresh(opts: RefreshLadderTimerOpts = {}): void {
  const g = globalThis as GlobalWithTimer;
  const handle = g[TIMER_PIN_KEY];
  if (handle === undefined) {
    g[STATE_PIN_KEY] = undefined;
    return;
  }
  const clear = getClearTimeout(opts.clearTimeoutFn);
  clear(handle);
  g[TIMER_PIN_KEY] = undefined;
  g[STATE_PIN_KEY] = undefined;
}

/**
 * Schedule (or re-schedule) the pre-expiry refresh.
 *
 * Behaviour:
 *
 *   1. Cancels any previously pinned timer.
 *   2. Computes the delay = `expiresAt − REFRESH_LEAD_TIME_MS − now`,
 *      floored at 0 (an already-overdue refresh fires immediately).
 *   3. Pins the new handle on `globalThis.__morfi_refreshTimer`.
 *   4. When the timer fires:
 *        a. Awaits `runRefresh()`.
 *        b. On `success: true` → re-arms with `outcome.nextExpiresAt`.
 *        c. On `success: false` → does NOT re-arm. The inner chain has
 *           already transitioned the connectivity FSM to
 *           `DEGRADED_NO_NATS(cause:'jwt-refresh-failed')` (see
 *           `connectivity/jwt-refresh.ts` step 7).
 *        d. On unexpected throw → treats as terminal failure (no re-arm).
 *           The FSM transition is the planner's responsibility; the
 *           ladder's only job here is to not leak a stale timer.
 *
 * Returns nothing — callers observe progress via the connectivity FSM.
 */
export function scheduleRefresh(args: ScheduleArgs): void {
  const setT = getSetTimeout(args.setTimeoutFn);
  const clearT = getClearTimeout(args.clearTimeoutFn);
  const now = getNow(args.now);

  // Step 1: clear any pinned predecessor.
  const g = globalThis as GlobalWithTimer;
  const prior = g[TIMER_PIN_KEY];
  if (prior !== undefined) {
    clearT(prior);
    g[TIMER_PIN_KEY] = undefined;
    g[STATE_PIN_KEY] = undefined;
  }

  // Step 2: compute delay (already-overdue → 0).
  const scheduledAt = now();
  const delay = timeUntilNextRefresh(args.expiresAt, scheduledAt);

  // Step 3-4: pin the timer.
  const handle = setT(() => {
    // The pinned handle is about to fire — clear the pin BEFORE the work
    // runs so a re-arm inside `runRefresh` (via a nested scheduleRefresh
    // call) doesn't clobber its own newly-pinned timer.
    g[TIMER_PIN_KEY] = undefined;
    g[STATE_PIN_KEY] = undefined;
    void runAndRearm(args);
  }, delay);
  g[TIMER_PIN_KEY] = handle;
  g[STATE_PIN_KEY] = {
    nextRefreshAt: scheduledAt + delay,
    scheduledAt,
    expiresAt: args.expiresAt,
  };
}

/**
 * After a failed refresh chain, keep trying every `REFRESH_FAILURE_RETRY_MS`
 * while the current token still has more than `REFRESH_FAILURE_GRACE_MS` of
 * life. Past that, the broker will drop the socket at expiry and the
 * `closed()` watcher + cold ladder rebuild with a fresh mint.
 */
function rearmAfterFailure(args: ScheduleArgs): void {
  const now = getNow(args.now)();
  const realExpiresAt = args.realExpiresAt ?? args.expiresAt;
  if (now >= realExpiresAt - REFRESH_FAILURE_GRACE_MS) return;
  scheduleRefresh({
    ...args,
    expiresAt: now + REFRESH_LEAD_TIME_MS + REFRESH_FAILURE_RETRY_MS,
    realExpiresAt,
  });
}

async function runAndRearm(args: ScheduleArgs): Promise<void> {
  let outcome: RefreshOutcome;
  try {
    outcome = await args.runRefresh();
  } catch {
    // The planner is expected to catch and report; if something escapes,
    // treat it like a failed chain and retry while the token lives.
    rearmAfterFailure(args);
    return;
  }
  if (!outcome.success) {
    rearmAfterFailure(args);
    return;
  }
  if (outcome.nextExpiresAt === undefined) {
    // Defensive: a success without an expiry is unrecoverable from the
    // ladder's perspective — we'd schedule against NaN. Treat as terminal.
    return;
  }
  // Step 4b: re-arm for the freshly minted JWT.
  const { realExpiresAt: _dropped, ...fresh } = args;
  scheduleRefresh({
    ...fresh,
    expiresAt: outcome.nextExpiresAt,
  });
}

/**
 * Test-only — drop the pinned timer without invoking clearTimeout. Used
 * by Vitest `afterEach` resetters that have already flushed fake timers.
 */
export function _resetRefreshLadderForTest(): void {
  const g = globalThis as GlobalWithTimer;
  g[TIMER_PIN_KEY] = undefined;
  g[STATE_PIN_KEY] = undefined;
}

/**
 * Read-only accessor — exposed for tests that need to assert "a timer is
 * currently pinned" without inspecting `globalThis` directly.
 */
export function _peekRefreshTimer(): unknown {
  const g = globalThis as GlobalWithTimer;
  return g[TIMER_PIN_KEY];
}

/**
 * Devtool peek — returns the wall-clock metadata for the currently
 * pinned refresh timer, or `undefined` when no refresh is scheduled.
 *
 * Useful for "when is the next JWT refresh?" surfaces; the raw timer
 * handle is opaque, this exposes the human-meaningful state.
 */
export function _peekRefreshState(): RefreshTimerState | undefined {
  const g = globalThis as GlobalWithTimer;
  return g[STATE_PIN_KEY];
}

/**
 * Re-export the lead-time helper so call sites can compute the same
 * `expiresAt − 10 min` delay the ladder uses without re-implementing it.
 */
export { timeUntilNextRefresh };
